// Dashboard date-range presets (L0-U16, ADR 0031 batch 4).
//
// The dashboard widgets all read the global URL date filter (since/until). Until
// a range is chosen the widgets show ALL-TIME data, which crowds fast. These
// presets give a one-click way to bound every widget, and the dashboard always
// opens on the current month-to-date (the default) so each visit starts from the
// same, current window.
//
// Ranges are computed in UTC (matching the widgets, which treat since/until as
// UTC day bounds) and exposed as pure functions so date-dependent tests can pin
// the clock (s295 pitfall).

import type { GlobalFilters } from "./globalFilters";

export type RangePresetKey = "mtd" | "7d" | "30d" | "90d" | "ytd" | "all";

export const DEFAULT_RANGE_PRESET: RangePresetKey = "mtd";

export interface RangePresetOption {
  key: RangePresetKey;
  label: string;
}

// Display order in the preset bar; also the precedence order for matchPreset
// when two presets resolve to the same window (e.g. "This month" and "YTD"
// both start Jan 1 in January -- the earlier entry wins).
export const RANGE_PRESETS: readonly RangePresetOption[] = [
  { key: "mtd", label: "This month" },
  { key: "7d", label: "7D" },
  { key: "30d", label: "30D" },
  { key: "90d", label: "90D" },
  { key: "ytd", label: "YTD" },
  { key: "all", label: "All" },
];

export interface PresetRange {
  since?: string;
  until?: string;
}

function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}

function toUtcDateString(d: Date): string {
  const year = d.getUTCFullYear().toString().padStart(4, "0");
  return `${year}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

// Resolve a preset to a concrete [since, until] window relative to `now`. "all"
// returns an empty object (both bounds undefined) so the global filter is
// cleared. Day-arithmetic goes through Date.UTC so month/year rollover is
// handled for the rolling windows.
export function computePresetRange(key: RangePresetKey, now: Date): PresetRange {
  if (key === "all") return {};
  const until = toUtcDateString(now);
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const d = now.getUTCDate();
  let since: string;
  switch (key) {
    case "mtd":
      since = `${y.toString().padStart(4, "0")}-${pad2(m + 1)}-01`;
      break;
    case "ytd":
      since = `${y.toString().padStart(4, "0")}-01-01`;
      break;
    case "7d":
      since = toUtcDateString(new Date(Date.UTC(y, m, d - 6)));
      break;
    case "30d":
      since = toUtcDateString(new Date(Date.UTC(y, m, d - 29)));
      break;
    case "90d":
      since = toUtcDateString(new Date(Date.UTC(y, m, d - 89)));
      break;
  }
  return { since, until };
}

// Which preset the current filters correspond to, or "custom" when the window
// matches none (e.g. a hand-picked range from the global filter toolbar).
export function matchPreset(
  filters: GlobalFilters,
  now: Date,
): RangePresetKey | "custom" {
  for (const preset of RANGE_PRESETS) {
    const range = computePresetRange(preset.key, now);
    if (filters.since === range.since && filters.until === range.until) {
      return preset.key;
    }
  }
  return "custom";
}
